import { useState, useEffect } from 'react'
import type { FileMetadata } from '../../../../types'

/**
 * What these handlers actually need from an event (SOL-82).
 *
 * Structural, not `React.MouseEvent`, so a KeyboardEvent is a legal argument
 * and keyboard selection runs this exact logic instead of a second copy of
 * the range and anchor rules. Both event types carry all four members; the
 * pointer-only ones (clientX, button, ...) were never read here.
 */
export type SelectionEvent = {
  shiftKey: boolean
  metaKey: boolean
  ctrlKey: boolean
  stopPropagation: () => void
}

export interface PhotoSelection {
  selectedPhotos: Set<string>
  handlePhotoSelect: (fileId: string, index: number, event: SelectionEvent) => void
  handleCheckboxClick: (fileId: string, index: number, event: SelectionEvent) => void
  handleSelectAll: () => void
  handleClearSelection: () => void
}

export function usePhotoSelection({
  albumFiles,
  resetKey,
}: {
  albumFiles: FileMetadata[]
  resetKey: string | undefined
}): PhotoSelection {
  // Photo selection state
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set())
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null)

  // Clear selection when navigating away from album
  useEffect(() => {
    setSelectedPhotos(new Set())
    setLastSelectedIndex(null)
  }, [resetKey])

  const handlePhotoSelect = (fileId: string, index: number, event: SelectionEvent) => {
    event.stopPropagation()

    const isShiftKey = event.shiftKey
    const isMetaKey = event.metaKey || event.ctrlKey

    setSelectedPhotos(prev => {
      const newSelection = new Set(prev)

      if (isShiftKey && lastSelectedIndex !== null && albumFiles.length > 0) {
        // Range selection
        const start = Math.min(lastSelectedIndex, index)
        const end = Math.max(lastSelectedIndex, index)
        for (let i = start; i <= end; i++) {
          newSelection.add(albumFiles[i].id)
        }
      } else if (isMetaKey) {
        // Toggle selection
        if (newSelection.has(fileId)) {
          newSelection.delete(fileId)
        } else {
          newSelection.add(fileId)
        }
      } else {
        // Single selection (if already selected and only one, deselect)
        if (newSelection.has(fileId) && newSelection.size === 1) {
          newSelection.clear()
        } else {
          newSelection.clear()
          newSelection.add(fileId)
        }
      }

      return newSelection
    })

    if (!event.shiftKey) {
      setLastSelectedIndex(index)
    }
  }

  const handleSelectAll = () => {
    if (albumFiles.length === 0) return
    setSelectedPhotos(new Set(albumFiles.map(f => f.id)))
  }

  const handleClearSelection = () => {
    setSelectedPhotos(new Set())
    setLastSelectedIndex(null)
  }

  const handleCheckboxClick = (fileId: string, index: number, event: SelectionEvent) => {
    event.stopPropagation()

    const isShiftKey = event.shiftKey

    setSelectedPhotos(prev => {
      const newSelection = new Set(prev)

      if (isShiftKey && lastSelectedIndex !== null && albumFiles.length > 0) {
        // Range selection
        const start = Math.min(lastSelectedIndex, index)
        const end = Math.max(lastSelectedIndex, index)
        for (let i = start; i <= end; i++) {
          newSelection.add(albumFiles[i].id)
        }
      } else {
        // Toggle selection
        if (newSelection.has(fileId)) {
          newSelection.delete(fileId)
        } else {
          newSelection.add(fileId)
        }
      }

      return newSelection
    })

    setLastSelectedIndex(index)
  }

  return {
    selectedPhotos,
    handlePhotoSelect,
    handleCheckboxClick,
    handleSelectAll,
    handleClearSelection,
  }
}
